import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import {
  captureReaderPosition,
  parseReaderPosition,
  readerScrollTarget,
  type ReaderPosition,
  type ReadingBlock,
} from './reader-position-helpers';

function readingBlocks(scroller: HTMLElement, content: HTMLElement): ReadingBlock[] {
  const origin = scroller.getBoundingClientRect().top;
  return [...content.querySelectorAll<HTMLElement>('[data-reader-anchor]')].map((element) => {
    const rect = element.getBoundingClientRect();
    return {
      id: element.dataset.readerAnchor!,
      top: rect.top - origin + scroller.scrollTop,
      height: rect.height,
    };
  });
}

export function useReaderPosition({
  userId,
  itemId,
  snapshotId,
  historical,
  scrollRef,
  contentRef,
  ready,
}: {
  userId: string;
  itemId: string;
  snapshotId?: string;
  historical: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  ready: boolean;
}) {
  const [notice, setNotice] = useState<'restored' | 'new-snapshot' | null>(null);
  const [storageFailed, setStorageFailed] = useState(false);
  const controller = useRef<{ preserve: () => void; reset: () => void } | null>(null);
  const key =
    snapshotId && userId ? `kiipu.reader-position.v1:${userId}:${itemId}:${snapshotId}` : null;
  useLayoutEffect(() => {
    setNotice(null);
    setStorageFailed(false);
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!ready || !key || !snapshotId || !scroller || !content) return;
    let alive = true;
    let correction: ReaderPosition | null = null;
    let blocks = readingBlocks(scroller, content);
    let latest = captureReaderPosition(snapshotId, 0, blocks);
    let saving: ReturnType<typeof setTimeout> | undefined;
    let adjusting = false;
    let frame = 0;
    let settleFrame = 0;
    let width = scroller.clientWidth;
    const fail = () => {
      if (alive) setStorageFailed(true);
    };
    const capture = () => captureReaderPosition(snapshotId, scroller.scrollTop, blocks);
    const persist = () => {
      try {
        const position = correction || latest;
        if (position.scrollTop < 60) localStorage.removeItem(key);
        else localStorage.setItem(key, JSON.stringify(position));
      } catch {
        fail();
      }
    };
    const adjust = (position: ReaderPosition) => {
      adjusting = true;
      scroller.scrollTop = readerScrollTarget(
        position,
        blocks,
        Math.max(0, scroller.scrollHeight - scroller.clientHeight),
      );
      latest = capture();
      cancelAnimationFrame(settleFrame);
      settleFrame = requestAnimationFrame(() => {
        adjusting = false;
      });
    };
    scroller.scrollTop = 0;
    try {
      const saved = parseReaderPosition(localStorage.getItem(key), snapshotId);
      if (saved && saved.scrollTop >= 60) {
        correction = saved;
        adjust(saved);
        setNotice('restored');
      }
      if (!historical) {
        const snapshotKey = `kiipu.reader-last-snapshot.v1:${userId}:${itemId}`;
        const previous = localStorage.getItem(snapshotKey);
        if (!saved && previous && previous !== snapshotId) setNotice('new-snapshot');
        localStorage.setItem(snapshotKey, snapshotId);
      }
    } catch {
      fail();
    }
    const correct = () => {
      if (!alive) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!alive) return;
        blocks = readingBlocks(scroller, content);
        if (correction) adjust(correction);
        else latest = capture();
      });
    };
    const stopCorrection = () => {
      correction = null;
    };
    const keyIntent = (event: KeyboardEvent) => {
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key))
        stopCorrection();
    };
    const onScroll = () => {
      if (!adjusting) correction = null;
      latest = capture();
      clearTimeout(saving);
      saving = setTimeout(persist, 200);
    };
    const resize = new ResizeObserver(() => {
      if (Math.abs(scroller.clientWidth - width) > 1) {
        width = scroller.clientWidth;
        correction = correction || latest;
      }
      correct();
    });
    resize.observe(scroller);
    resize.observe(content);
    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('wheel', stopCorrection, { passive: true });
    scroller.addEventListener('touchmove', stopCorrection, { passive: true });
    scroller.addEventListener('keydown', keyIntent);
    content.addEventListener('load', correct, true);
    content.addEventListener('error', correct, true);
    void document.fonts?.ready.then(() => {
      if (alive) correct();
    });
    window.addEventListener('pagehide', persist);
    controller.current = {
      preserve: () => {
        correction = capture();
        latest = correction;
        correct();
      },
      reset: () => {
        correction = null;
        clearTimeout(saving);
        adjust(captureReaderPosition(snapshotId, 0, []));
        setNotice(null);
        persist();
      },
    };
    return () => {
      persist();
      alive = false;
      controller.current = null;
      clearTimeout(saving);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(settleFrame);
      resize.disconnect();
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('wheel', stopCorrection);
      scroller.removeEventListener('touchmove', stopCorrection);
      scroller.removeEventListener('keydown', keyIntent);
      content.removeEventListener('load', correct, true);
      content.removeEventListener('error', correct, true);
      window.removeEventListener('pagehide', persist);
    };
  }, [key, ready, historical, userId, itemId, snapshotId, scrollRef, contentRef]);
  const preservePosition = useCallback(() => controller.current?.preserve(), []);
  const startOver = useCallback(() => controller.current?.reset(), []);
  return { notice, storageFailed, preservePosition, startOver };
}
